import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AppState, type AppStateStatus } from 'react-native';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

type ActionBreakdown = {
  agreements: number;
  offers: number;
  sentOffers: number;
  connections: number;
  unreadEvents: number;
  circleSize: number;
  unreadChat: number;
  total: number;
};

type NeedsActionContextValue = {
  count: number;           // alias για total (backwards compat)
  breakdown: ActionBreakdown;
  refresh: () => void;
};

const DEFAULT: ActionBreakdown = { agreements: 0, offers: 0, sentOffers: 0, connections: 0, unreadEvents: 0, circleSize: 0, unreadChat: 0, total: 0 };

const NeedsActionContext = createContext<NeedsActionContextValue>({
  count: 0,
  breakdown: DEFAULT,
  refresh: () => {},
});

export function NeedsActionProvider({ children }: { children: React.ReactNode }) {
  const { session } = useAuth();
  const [breakdown, setBreakdown] = useState<ActionBreakdown>(DEFAULT);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const refresh = useCallback(async () => {
    if (!session) { setBreakdown(DEFAULT); return; }
    try {
      const { data } = await supabase.rpc('get_action_breakdown');
      if (data && typeof data === 'object') {
        const ag = Number((data as any).agreements ?? 0);
        const of = Number((data as any).offers ?? 0);
        const so = Number((data as any).sent_offers ?? 0);
        const cn = Number((data as any).connections ?? 0);
        const ue = Number((data as any).unread_events ?? 0);
        const cs = Number((data as any).circle_size ?? 0);
        const uc = Number((data as any).unread_chat ?? 0);
        setBreakdown({ agreements: ag, offers: of, sentOffers: so, connections: cn, unreadEvents: ue, circleSize: cs, unreadChat: uc, total: ag + of + cn + ue });
      }
    } catch {
      // silent fail
    }
  }, [session]);

  useEffect(() => {
    void refresh();
    intervalRef.current = setInterval(() => void refresh(), 60_000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [refresh]);

  useEffect(() => {
    const sub = AppState.addEventListener('change', (state: AppStateStatus) => {
      if (state === 'active') void refresh();
    });
    return () => sub.remove();
  }, [refresh]);

  return (
    <NeedsActionContext.Provider value={{ count: breakdown.total, breakdown, refresh }}>
      {children}
    </NeedsActionContext.Provider>
  );
}

export function useNeedsAction() {
  return useContext(NeedsActionContext);
}
